import { Button } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import classNames from 'classnames';
import { useAppFlags } from 'utils/useAppFlags';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getSelectedPersonVitalInsights } from '@redux/person/personSelector';
import { getPersonVitalInsightsThunk } from '@redux/person/personThunks';
import { PersonVitalTimeFrame, PersonVitalType } from '@redux/person/personTypes';
import { ReduxState } from '@redux/types';
import { EnumSelect } from 'components/EnumSelect/EnumSelect';
import { Text } from 'components/Typography';
import { Charts } from 'containers/PatientDashboard/components/Charts/Charts';
import { InsightsGrid } from '../InsightsGrid/InsightsGrid';
import { SleepInsightsGrid } from '../InsightsGrid/SleepInsightsGrid';
import style from './Insights.scss';
import { customDateTimePickerStyles } from '../../../../customStyles';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & {
  personId: string;
  timeZoneId: string;
};

const Insights: React.FC<Props> = ({ personId, timeZoneId, selectedPersonVitalInsights, getPersonVitalInsights }) => {
  const flags = useAppFlags();
  const isAlzheimersStudy = flags?.alzheimersSleepStudy ?? false;
  const [vitalType, setVitalType] = useState<PersonVitalType>(isAlzheimersStudy ? PersonVitalType.NIGHT_SLEEP_TIME : PersonVitalType.HEART_RATE);
  const [timeFrame, setTimeFrame] = useState<PersonVitalTimeFrame>(PersonVitalTimeFrame.SEVEN_DAYS);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [hasChartData, setHasChartData] = useState<boolean>(true);
  const { patientMetrics, sleepPatientMetrics, listOfAggregatedTotalSleepData, controlLevel } = isLoadingData ? {} : (selectedPersonVitalInsights ?? {});
  const [selectedDate, setSelectedDate] = useState<Date>(() => moment().tz(timeZoneId).toDate());

  const onSelectionChange = useCallback(
    (selected: string) => {
      setIsLoadingData(true);
      setVitalType(selected as PersonVitalType);
      if (
        [PersonVitalType.NIGHT_SLEEP_TIME, PersonVitalType.NIGHT_AWAKE_TIME, PersonVitalType.BED_EXITS, PersonVitalType.DAY_SLEEP_TIME].includes(selected as PersonVitalType) &&
        ![PersonVitalTimeFrame.SEVEN_DAYS, PersonVitalTimeFrame.THIRTY_DAYS].includes(timeFrame)
      ) {
        setTimeFrame(PersonVitalTimeFrame.SEVEN_DAYS);
      }
    },
    [timeFrame]
  );

  const onTimeFrameChange = useCallback(
    (selected: string) => {
      if (selected === timeFrame) return;

      setIsLoadingData(true);
      setTimeFrame(selected as PersonVitalTimeFrame);
    },
    [timeFrame]
  );

  const onDaySelect = useCallback((date: Date) => {
    setIsLoadingData(true);
    setSelectedDate(date);
    setTimeFrame(PersonVitalTimeFrame.TWENTY_FOUR_HOURS);
  }, []);

  const onChartDataAvailabilityChange = useCallback((hasData: boolean) => {
    setHasChartData(hasData);
  }, []);

  useEffect(() => {
    const getData = async () => {
      setIsLoadingData(true);
      await getPersonVitalInsights(personId, vitalType, timeFrame, selectedDate, timeZoneId);
      setIsLoadingData(false);
    };

    getData();
  }, [vitalType, timeFrame, personId, selectedDate]);

  // Use default order when not in Alzheimer's study, otherwise prefer sleep-related metrics
  const enumData: { [key in string]: string } = isAlzheimersStudy
    ? {
        NIGHT_SLEEP_TIME: PersonVitalType.NIGHT_SLEEP_TIME,
        NIGHT_AWAKE_TIME: PersonVitalType.NIGHT_AWAKE_TIME,
        BED_EXITS: PersonVitalType.BED_EXITS,
        DAY_SLEEP_TIME: PersonVitalType.DAY_SLEEP_TIME,
        HEART_RATE: PersonVitalType.HEART_RATE,
        BREATHING_RATE: PersonVitalType.BREATHING_RATE,
        SLEEP: PersonVitalType.SLEEP
      }
    : {
        HEART_RATE: PersonVitalType.HEART_RATE,
        BREATHING_RATE: PersonVitalType.BREATHING_RATE,
        SLEEP: PersonVitalType.SLEEP,
        NIGHT_SLEEP_TIME: PersonVitalType.NIGHT_SLEEP_TIME,
        NIGHT_AWAKE_TIME: PersonVitalType.NIGHT_AWAKE_TIME,
        BED_EXITS: PersonVitalType.BED_EXITS,
        DAY_SLEEP_TIME: PersonVitalType.DAY_SLEEP_TIME
      };

  return (
    <div className={style.topContainer}>
      <div className={style.leftContainer}>
        <div className={style.insightsTextContainer}>
          <Text variant="xl">Insights</Text>
        </div>
        <div>
          <Text variant="sm" className={style.defaultGrayText}>
            Select related data
          </Text>
          <EnumSelect
            enumData={enumData}
            onSelectionChange={onSelectionChange}
            initialSelection={isAlzheimersStudy ? PersonVitalType.NIGHT_SLEEP_TIME : PersonVitalType.HEART_RATE}
          />
          {vitalType === PersonVitalType.SLEEP && (
            <SleepInsightsGrid
              containerClassName={style.metricsContainer}
              patientMetrics={sleepPatientMetrics}
              listOfAggregatedTotalSleepData={listOfAggregatedTotalSleepData}
              controlLevel={controlLevel}
              timeFrame={timeFrame}
              hasChartData={hasChartData}
            />
          )}
          {[PersonVitalType.HEART_RATE, PersonVitalType.BREATHING_RATE].includes(vitalType) && (
            <InsightsGrid containerClassName={style.metricsContainer} patientMetrics={patientMetrics} controlLevel={controlLevel} hasChartData={hasChartData} />
          )}
        </div>
      </div>

      <div className={style.rightContainer}>
        <div className={style.durationHeader}>
          <div>
            {Object.keys(
              [PersonVitalType.NIGHT_SLEEP_TIME, PersonVitalType.NIGHT_AWAKE_TIME, PersonVitalType.BED_EXITS, PersonVitalType.DAY_SLEEP_TIME].includes(vitalType)
                ? ({
                    SEVEN_DAYS: PersonVitalTimeFrame.SEVEN_DAYS,
                    THIRTY_DAYS: PersonVitalTimeFrame.THIRTY_DAYS
                  } as const)
                : PersonVitalTimeFrame
            ).map((key, index) => {
              return (
                <Button
                  key={index}
                  className={classNames(style.tab, { [style.active]: timeFrame === PersonVitalTimeFrame[key] })}
                  onClick={() => onTimeFrameChange(PersonVitalTimeFrame[key])}
                >
                  {PersonVitalTimeFrame[key]}
                </Button>
              );
            })}
          </div>
          <div style={{ paddingRight: '20px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={selectedDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    setSelectedDate(date);
                  }
                }}
                disableFuture={true}
                timezone={timeZoneId}
                sx={customDateTimePickerStyles}
              />
            </LocalizationProvider>
          </div>
        </div>
        <Charts
          isLoadingData={isLoadingData}
          timeZoneId={timeZoneId}
          personVitalInsights={selectedPersonVitalInsights}
          vitalType={vitalType}
          timeFrame={timeFrame}
          selectedDate={selectedDate}
          onDaySelect={onDaySelect}
          minRate={patientMetrics?.minRate}
          maxRate={patientMetrics?.maxRate}
          minControlRate={controlLevel?.normalMinRate}
          maxControlRate={controlLevel?.normalMaxRate}
          onChartDataAvailabilityChange={onChartDataAvailabilityChange}
        />
      </div>
    </div>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    selectedPersonVitalInsights: getSelectedPersonVitalInsights(state)
  }),
  {
    getPersonVitalInsights: getPersonVitalInsightsThunk
  }
);

export default compose(connectRedux)(Insights);
